import React, { useCallback, useEffect, useRef, useState } from "react";
import { AttachmentChip } from "./AttachmentChip";

/** What the chip needs from the documents API (a slice of DocumentsApi). */
export interface AttachmentDocumentsApi {
  get(id: number): Promise<{
    status: string;
    can_write?: boolean;
    filename?: string;
    duplicate_of_document_id?: number | null;
  }>;
  promote(id: number): Promise<unknown>;
}

export interface AttachmentKnowledgeChipProps {
  documentId: number;
  name: string;
  mime?: string;
  api: AttachmentDocumentsApi;
  /** How often to re-read the document while it is being indexed. */
  pollIntervalMs?: number;
}

/** Where the attachment stands relative to the knowledge base.
 *  - `attachment`: a chat attachment (`extracted`) the caller may add.
 *  - `processing`: being indexed after the caller added it.
 *  - `added`: the caller added it here and indexing finished.
 *  - `in_kb`: already in the knowledge base when the chip loaded.
 *  - `duplicate_personal` / `duplicate`: its content is already in the
 *    knowledge base as another document (named when it can be read).
 *  - `failed`: the caller's promote failed; the action is offered again.
 *  - `plain`: anything else — not writable, still extracting, or the lookup
 *    failed (fail safe: a plain chip, never a wrong action). */
type KbState =
  | "loading"
  | "attachment"
  | "processing"
  | "added"
  | "in_kb"
  | "duplicate_personal"
  | "duplicate"
  | "failed"
  | "plain";

function noteFor(state: KbState, original: string | null): string | undefined {
  switch (state) {
    case "processing":
      return "Processing…";
    case "added":
      return "In your knowledge base";
    case "in_kb":
      return "In knowledge base";
    case "duplicate_personal":
      return original ? `Already in your knowledge base as ${original}` : "Already in your knowledge base";
    case "duplicate":
      return original ? `Already in the knowledge base as ${original}` : "Already in the knowledge base";
    case "failed":
      return "Couldn't add to knowledge base";
    default:
      return undefined;
  }
}

// Indexing can take minutes for a large file; stop polling after this so a
// stuck job does not poll forever (the chip then stays "Processing…").
const MAX_POLL_MS = 10 * 60 * 1000;

/**
 * An attachment chip on a sent user message that knows whether the file is in
 * the knowledge base (spec 2026-10-05 §3.4, entry point 1). It offers "Add to
 * knowledge base" only for a chat attachment the caller may write — the same
 * rule the promote endpoint enforces, which re-checks on every call.
 */
export function AttachmentKnowledgeChip({
  documentId,
  name,
  mime,
  api,
  pollIntervalMs = 2000,
}: AttachmentKnowledgeChipProps) {
  const [state, setState] = useState<KbState>("loading");
  const [original, setOriginal] = useState<string | null>(null);
  const alive = useRef(true);

  /** Show a duplicate outcome, naming the original when the caller can read
   *  it (the GET is the server's read gate; a refusal just leaves it unnamed). */
  const showDuplicate = useCallback(
    (status: "duplicate" | "duplicate_personal", originalId?: number | null) => {
      setState(status);
      if (originalId == null) return;
      api
        .get(originalId)
        .then((row) => {
          if (alive.current && row.filename) setOriginal(row.filename);
        })
        .catch(() => {});
    },
    [api],
  );

  useEffect(() => {
    alive.current = true;
    api
      .get(documentId)
      .then((row) => {
        if (!alive.current) return;
        if (row.status === "ready") setState("in_kb");
        else if (row.status === "duplicate" || row.status === "duplicate_personal")
          showDuplicate(row.status, row.duplicate_of_document_id);
        else if (row.status === "extracted" && row.can_write) setState("attachment");
        else setState("plain");
      })
      .catch(() => {
        if (alive.current) setState("plain");
      });
    return () => {
      alive.current = false;
    };
  }, [api, documentId, showDuplicate]);

  const waitUntilIndexed = useCallback(async () => {
    const deadline = Date.now() + MAX_POLL_MS;
    while (alive.current && Date.now() < deadline) {
      await new Promise((r) => setTimeout(r, pollIntervalMs));
      if (!alive.current) return;
      try {
        const row = await api.get(documentId);
        if (!alive.current) return;
        if (row.status === "ready") {
          setState("added");
          return;
        }
        if (row.status === "duplicate" || row.status === "duplicate_personal") {
          showDuplicate(row.status, row.duplicate_of_document_id);
          return;
        }
        if (row.status === "failed") {
          // The re-index failed: say so and let the caller try again (the
          // promote route re-indexes a failed document).
          setState("failed");
          return;
        }
      } catch {
        // A transient read error: keep waiting rather than mislabel the file.
      }
    }
  }, [api, documentId, pollIntervalMs, showDuplicate]);

  const promote = useCallback(async () => {
    setState("processing");
    try {
      await api.promote(documentId);
    } catch {
      // Nothing was started: offer the action again so the user can retry.
      if (alive.current) setState("attachment");
      return;
    }
    void waitUntilIndexed();
  }, [api, documentId, waitUntilIndexed]);

  return (
    <AttachmentChip
      name={name}
      mime={mime}
      status="ready"
      note={noteFor(state, original)}
      onPromote={state === "attachment" || state === "failed" ? () => void promote() : undefined}
    />
  );
}
